/**
 * useVendors Hook
 * ahooks data fetching hook for vendor list with filters, pagination, and sorting
 */

import { useMemo } from 'react';
import { useRequest, useAntdTable, clearCache } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { vendorApi } from '@/services/api';
import { useVendorStore, useVendorQueryParams } from '@/stores';
import type {
  VendorListResponse,
  VendorDetailResponse,
  VendorLocationsResponse,
  VendorLocationResponse,
  CheckExistsResponse,
  VendorListParams,
  VendorSortField,
  VendorLocationListParams,
} from '@/types/api';
import { getCacheKey } from '@/utils/storeFirstData';
import { trackCacheKey, clearCacheWhere } from '@/utils/cacheKeyRegistry';

/**
 * Helper to invalidate all vendor-related caches
 * Cache key format: /api/vendors?{params}
 */
const invalidateVendorListCaches = () => {
  // Clear all vendor list caches (exact-key clear of every tracked list key —
  // the old predicate form was a silent no-op; see cacheKeyRegistry.ts)
  clearCacheWhere((key) => key.startsWith('/api/vendors?'));
  // Static shared dropdown-options key (all useVendorOptions consumers)
  // — not registry-tracked, so clear it exactly.
  clearCache('/api/vendors-options');
};

/**
 * Helper to invalidate a specific vendor's detail cache
 * Cache key format: /api/vendors/${vendorId}
 */
const invalidateVendorDetailCache = (vendorId: number) => {
  clearCache(`/api/vendors/${vendorId}`);
};

/**
 * Helper to invalidate both list and detail caches for a vendor
 */
const invalidateVendorCaches = (vendorId: number) => {
  invalidateVendorDetailCache(vendorId);
  invalidateVendorListCaches();
};

/**
 * Helper to invalidate vendor location caches
 * Cache key format: /api/vendors/${vendorId}/locations
 */
export const invalidateVendorLocationCaches = (vendorId: number) => {
  clearCacheWhere((key) => key.includes(`/api/vendors/${vendorId}/locations`));
};

/**
 * Hook to fetch all active vendors for dropdown options (product wizard +
 * vendor-line modal, PO list pages). Mirrors `useCustomerOptions`.
 *
 * Uses the shared cache key '/api/vendors-options' — every consumer must go
 * through this hook so the fetch params can never diverge (divergent params
 * on a shared cacheKey mean whichever fetch runs last poisons the others).
 */
export function useVendorOptions(options?: { ready?: boolean }) {
  const { data, error, loading } = useRequest(
    () => vendorApi.list({ isActive: 1, pageSize: -1 }), // Fetch all active vendors (API spec: -1 = all)
    {
      ready: options?.ready ?? true,
      cacheKey: '/api/vendors-options',
      staleTime: 60000, // 1 minute cache
    }
  );

  const vendors = useMemo(() => data?.data ?? [], [data]);

  return {
    vendors,
    isLoading: loading,
    error,
  };
}

/**
 * Hook to fetch vendor list with automatic state management
 * Syncs with Zustand store for filters, pagination, and sorting
 */
export function useVendors(config?: Options<VendorListResponse, []>) {
  // Get query params from store
  const params = useVendorQueryParams();
  const { setVendors, setPagination, setLoading, setError } = useVendorStore();

  // Fetch vendors with ahooks
  const { data, error, loading, refresh } = useRequest(() => vendorApi.list(params), {
    refreshDeps: [
      params.page,
      params.pageSize,
      params.search,
      params.categoryIds,
      params.country,
      params.isActive,
      params.sortBy,
      params.sortOrder,
    ],
    cacheKey: trackCacheKey(`/api/vendors?${JSON.stringify(params)}`),
    staleTime: 5000, // 5 seconds (reduced for better responsiveness)
    ...config,
    onSuccess: (data) => {
      // Handle nullable data from API
      const vendors = data.data ?? [];
      const pagination = data.pagination ?? { page: 1, pageSize: 10, total: 0, totalPages: 0 };
      setVendors(vendors);
      setPagination(pagination);
      setLoading(false);
      setError(null);
      config?.onSuccess?.(data, []);
    },
    onError: (err) => {
      setLoading(false);
      setError(err.message || 'Failed to fetch vendors');
      config?.onError?.(err, []);
    },
  });

  return {
    vendors: data?.data ?? [],
    pagination: data?.pagination,
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook using useAntdTable for seamless Ant Design Table integration
 * Returns tableProps that can be spread directly onto <Table>
 *
 * @param filters - External filters (search, categoryIds, country, isActive) from store or local state
 */
export function useVendorsTable(filters: {
  search?: string;
  categoryIds?: number[];
  country?: string;
  isActive?: boolean | null;
}) {
  const { setVendors, setPagination } = useVendorStore();

  // Map column field names to API sortBy values (numeric per API spec)
  // API sortBy: 1=vendorId, 2=vendorName, 3=vendorCode, 4=abn, 5=address, 6=phone, 7=isActive
  const fieldToSortBy: Record<string, VendorSortField> = {
    vendorId: 1,
    vendorName: 2,
    vendorCode: 3,
    abn: 4,
    address: 5,
    phone: 6,
    isActive: 7,
  };

  // Convert filters to API format
  const getApiParams = (): Partial<VendorListParams> => {
    let isActiveParam: number | undefined;
    if (filters.isActive === true) {
      isActiveParam = 1;
    } else if (filters.isActive === false) {
      isActiveParam = 2;
    } else {
      isActiveParam = 3; // null means all
    }

    return {
      search: filters.search || undefined,
      categoryIds:
        filters.categoryIds && filters.categoryIds.length > 0
          ? filters.categoryIds.join(',')
          : undefined,
      country: filters.country || undefined,
      isActive: isActiveParam,
    };
  };

  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize, sorter }) => {
      // Map sorter to API format (numeric per API spec)
      // Default: sort by vendorId descending (newest first)
      let sortBy: VendorSortField = 1; // vendorId
      let sortOrder: 0 | 1 = 1; // descend

      if (sorter && 'field' in sorter && sorter.order) {
        sortBy = fieldToSortBy[sorter.field as string] ?? 1;
        sortOrder = sorter.order === 'descend' ? 1 : 0;
      }

      const params: VendorListParams = {
        page: current,
        pageSize,
        ...getApiParams(),
        sortBy,
        sortOrder,
      };

      const response = await vendorApi.list(params);

      // Sync with Zustand store - handle nullable data
      const data = response.data ?? [];
      const pagination = response.pagination ?? { page: 1, pageSize: 10, total: 0, totalPages: 0 };

      setVendors(data);
      setPagination(pagination);

      return {
        total: pagination.total,
        list: data,
      };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [
        filters.search,
        JSON.stringify(filters.categoryIds),
        filters.country,
        filters.isActive,
      ],
    }
  );

  return {
    tableProps,
    refresh: () => {
      // Clear caches and refresh
      invalidateVendorListCaches();
      refresh();
    },
    isLoading: loading || params.length === 0,
    error,
  };
}

/**
 * Hook to fetch vendor detail by ID
 */
export function useVendorDetail(
  vendorId: number | null,
  config?: Options<VendorDetailResponse, []>
) {
  const { data, error, loading, mutate, run, refresh } = useRequest(
    () => vendorApi.getById(vendorId!),
    {
      ready: !!vendorId,
      cacheKey: vendorId ? `/api/vendors/${vendorId}` : undefined,
      staleTime: 5000, // 5 seconds (reduced for better responsiveness)
      ...config,
    }
  );

  // Consider it loading if:
  // 1. loading is true, OR
  // 2. vendorId is provided but data hasn't arrived yet AND no error occurred
  const isLoading = loading || (!!vendorId && !data && !error);

  return {
    vendor: data,
    error,
    isLoading,
    mutate, // ahooks mutate - for direct data manipulation
    run, // run forces a new request, bypassing cache
    refresh, // ahooks refresh - re-runs with same params
  };
}

/**
 * Hook to fetch vendor locations by vendor ID
 */
export function useVendorLocations(
  vendorId: number | null,
  params?: VendorLocationListParams,
  config?: Options<VendorLocationsResponse, []>
) {
  const { data, error, loading, refresh } = useRequest(
    () => vendorApi.getLocations(vendorId!, params),
    {
      ready: !!vendorId,
      refreshDeps: [vendorId, JSON.stringify(params)],
      cacheKey: vendorId
        ? trackCacheKey(getCacheKey(`/api/vendors/${vendorId}/locations`, params))
        : undefined,
      staleTime: 5000, // 5 seconds (reduced for better responsiveness)
      ...config,
      // Handle 404 errors gracefully - locations API may not exist yet on backend
      onError: (err) => {
        // Log error but don't propagate - return empty array instead
        console.warn(
          `[useVendorLocations] Failed to fetch locations for vendor ${vendorId}:`,
          err.message
        );
        config?.onError?.(err, []);
      },
    }
  );

  return {
    locations: data?.data ?? [],
    error,
    isLoading: loading,
    refresh,
  };
}

/**
 * Hook to fetch a single vendor location by ID
 * Used for edit modal to get raw location data
 */
export function useVendorLocationDetail(
  vendorId: number | null,
  locationId: number | null,
  config?: Options<VendorLocationResponse, []>
) {
  const { data, error, loading, refresh } = useRequest(
    () => vendorApi.getLocationById(vendorId!, locationId!),
    {
      ready: !!vendorId && !!locationId,
      refreshDeps: [vendorId, locationId],
      cacheKey:
        vendorId && locationId
          ? trackCacheKey(`/api/vendors/${vendorId}/locations/${locationId}`)
          : undefined,
      staleTime: 5000, // 5 seconds
      ...config,
    }
  );

  return {
    location: data ?? null,
    error,
    isLoading: loading,
    refresh,
  };
}

/**
 * Hook to check if vendor exists (for validation)
 * Uses countryId and localTaxId to check vendor existence
 */
export function useCheckVendorExists(
  countryId: number | null,
  localTaxId: string | null,
  config?: Options<CheckExistsResponse, []>
) {
  const { data, error, loading } = useRequest(
    () => vendorApi.checkExists({ countryId: countryId!, localTaxId: localTaxId! }),
    {
      ready: !!(countryId && localTaxId),
      // Re-fetch when countryId or localTaxId changes
      refreshDeps: [countryId, localTaxId],
      cacheKey:
        countryId && localTaxId
          ? `/api/vendors/check-exists/${countryId}/${localTaxId}`
          : undefined,
      cacheTime: 1000,
      ...config,
    }
  );

  return {
    exists: data?.exists ?? false,
    companyExists: data?.companyExists ?? false,
    existingVendorId: data?.vendorId,
    existingVendorName: data?.vendorName,
    existingCompany: data?.company,
    message: data?.message,
    isLoading: loading,
    error,
  };
}

/**
 * Hook for vendor mutations (create, update, delete, toggle status)
 * All mutations invalidate ahooks cache to ensure UI stays in sync
 */
export function useVendorMutations() {
  const { updateVendor, removeVendor } = useVendorStore();

  const createVendor = async (data: Parameters<typeof vendorApi.create>[0]) => {
    const response = await vendorApi.create(data);
    // Invalidate list cache to show new vendor
    invalidateVendorListCaches();
    return response;
  };

  const quickCreateVendor = async (data: Parameters<typeof vendorApi.quickCreate>[0]) => {
    const response = await vendorApi.quickCreate(data);
    // Invalidate list cache to show new vendor
    invalidateVendorListCaches();
    return response;
  };

  const updateVendorData = async (
    vendorId: number,
    data: Parameters<typeof vendorApi.update>[1]
  ) => {
    const response = await vendorApi.update(vendorId, data);
    updateVendor(vendorId, response);
    // Invalidate both detail and list caches
    invalidateVendorCaches(vendorId);
    return response;
  };

  const toggleVendorStatus = async (vendorId: number, isActive: boolean) => {
    const response = await vendorApi.toggleStatus(vendorId, isActive);
    updateVendor(vendorId, response);
    // Invalidate both detail and list caches
    invalidateVendorCaches(vendorId);
    return response;
  };

  const deleteVendor = async (vendorId: number) => {
    // Note: API doesn't have delete endpoint, but we can prepare for it
    removeVendor(vendorId);
    // Invalidate both detail and list caches
    invalidateVendorCaches(vendorId);
  };

  return {
    createVendor,
    quickCreateVendor,
    updateVendor: updateVendorData,
    toggleStatus: toggleVendorStatus,
    deleteVendor,
  };
}

/**
 * Hook for vendor location mutations (create, update, toggle status, set default)
 * All mutations invalidate ahooks cache to ensure UI stays in sync
 */
export function useLocationMutations(vendorId: number) {
  const createLocation = async (data: Parameters<typeof vendorApi.createLocation>[1]) => {
    const response = await vendorApi.createLocation(vendorId, data);
    // Invalidate cache to refresh list
    invalidateVendorLocationCaches(vendorId);
    return response;
  };

  const updateLocation = async (
    locationId: number,
    data: Parameters<typeof vendorApi.updateLocation>[2]
  ) => {
    const response = await vendorApi.updateLocation(vendorId, locationId, data);
    // Invalidate cache to refresh list
    invalidateVendorLocationCaches(vendorId);
    return response;
  };

  const toggleLocationStatus = async (locationId: number, isActive: boolean) => {
    const response = await vendorApi.toggleLocationStatus(vendorId, locationId, isActive);
    // Invalidate cache to refresh list with updated status
    invalidateVendorLocationCaches(vendorId);
    return response;
  };

  const setDefaultLocation = async (locationId: number, isDefault: boolean) => {
    const response = await vendorApi.setDefaultLocation(vendorId, locationId, isDefault);
    // Invalidate cache to refresh list with updated default
    invalidateVendorLocationCaches(vendorId);
    return response;
  };

  return {
    createLocation,
    updateLocation,
    toggleStatus: toggleLocationStatus,
    setDefault: setDefaultLocation,
  };
}
